import { shouldTrackBeUpdated } from '@/helpers/index';
import SpotifyUserService from '@/services/api/SpotifyUserService';
import PlaylistTracksService from '@/services/api/PlaylistTracksService';
import TrackMapper from '@/services/mapper/TrackMapper';

export default {

  actions: {
    toggleTrack({ commit, getters, dispatch }, track) {
      const tracks = getters.currentEvent.playlist.tracks.slice();
      const shouldBeUpdated = shouldTrackBeUpdated(tracks, track);
      const payload = {
        track,
      };

      let promise;
      // check if track is already stored
      // when this is the case, go ahead the process
      // if its not, add the track to the stored tracks array
      if (tracks.some(t => t.track_id === track.track_id)) {
        const index = tracks.findIndex(t => t.track_id === track.track_id);
        // check if track is already transferred
        // when this is the case, remove it from stored tracks
        // if its not the case, update the is_transferred property
        // and replace the entire track object in the stored tracks array
        if (shouldBeUpdated) {
          const updatedTrack = tracks[index];
          updatedTrack.is_transferred = true;
          tracks.splice(index, 1, updatedTrack);
          promise = dispatch('updateTrackTransferredStatusOnPlaylist', track);
        } else {
          tracks.splice(index, 1);
          promise = dispatch('removeTrackFromPlaylist', payload);
        }
      } else {
        tracks.push(track);
        promise = dispatch('addTrackToPlaylist', payload);
      }

      commit('updateCurrentEventTracks', { eventId: getters.currentEvent.id, tracks });
      return new Promise((resolve, reject) => {
        promise
          .then(resolve)
          .catch(reject);
      });
    },

    addTrackToPlaylist({ getters, dispatch }, { track }) {
      return new Promise((resolve, reject) => {
        if (getters.isDefaultUser) {
          dispatch('addTrackToPlaylistAsUser', { track })
            .then(data => resolve(data))
            .catch(error => reject(error));
        } else {
          dispatch('addTrackToPlaylistAsSubUser', { track })
            .then(data => resolve(data))
            .catch(error => reject(error));
        }
      });
    },

    removeTrackFromPlaylist({ getters, dispatch }, { track }) {
      return new Promise((resolve, reject) => {
        PlaylistTracksService.destroy(getters.playlistId, track)
          .then(freshTracks => {
            SpotifyUserService.removePlaylistTrack(getters.spotifyPlaylistId, [track.uri]);
            dispatch('fetchEvents');
            resolve(freshTracks);
          })
          .catch(error => reject(error));
      });
    },

    addTrackToPlaylistAsUser({ getters, dispatch }, { track }) {
      return new Promise((resolve, reject) => {
        PlaylistTracksService.store(getters.playlistId, track)
          .then(tracks => {
            const currentTrack = tracks.find(t => t.track_id === track.track_id);

            dispatch('updateSpotifyPlaylistTracks', { uris: [currentTrack.uri] })
              .then(() => {
                dispatch('updatePlaylistTrackTransferStatus', {
                  tracks: { tracks: [currentTrack] },
                })
                  .then(() => {
                    dispatch('fetchEvents');
                  });
              });

            resolve(tracks);
          })
          .catch(error => reject(error));
      });
    },

    addTrackToPlaylistAsSubUser({ getters, dispatch }, { track }) {
      return new Promise((resolve, reject) => {
        PlaylistTracksService.store(getters.playlistId, track)
          .then(tracks => {
            dispatch('fetchEvents');
            resolve(tracks);
          })
          .catch(error => reject(error));
      });
    },

    updateTrackTransferredStatusOnPlaylist({ getters, dispatch }, track) {
      return new Promise((resolve, reject) => {
        dispatch('updateSpotifyPlaylistTracks', { uris: [track.uri] })
          .then(() => {
            dispatch('updatePlaylistTrackTransferStatus', {
              tracks: { tracks: [track] },
            })
              .then(updatedTracks => {
                dispatch('fetchEvents');
                resolve(updatedTracks);
              });
          })
          .catch(error => reject(error));
      });
    },

    updateSpotifyPlaylistTracks({ getters }, { uris }) {
      return new Promise((resolve, reject) => {
        SpotifyUserService.addPlaylistTrack(getters.spotifyPlaylistId, uris)
          .then(data => {
            resolve(data);
          })
          .catch(error => reject(error));
      });
    },

    updatePlaylistTrackTransferStatus({ getters }, { tracks }) {
      return new Promise((resolve, reject) => {
        if (tracks.tracks.length <= 0) {
          reject();
          return;
        }
        PlaylistTracksService.update(getters.playlistId, tracks)
          .then(data => {
            resolve(data);
          })
          .catch(error => reject(error));
      });
    },

    createSpotifyPlaylist({ getters }, name) {
      return new Promise((resolve, reject) => {
        SpotifyUserService.createPlaylist(getters.spotifyUser.id, name)
          .then(playlist => resolve(playlist))
          .catch(error => reject(error));
      });
    },

    updateSpotifyPlaylist({ getters }, { playlistId, params }) {
      return new Promise((resolve, reject) => {
        SpotifyUserService.updatePlaylist(getters.spotifyUser.id, playlistId, params)
          .then(({ data }) => {
            resolve(data);
          })
          .catch(error => reject(error));
      });
    },

    unfollowSpotifyPlaylist({ getters }) {
      return new Promise((resolve, reject) => {
        SpotifyUserService.unfollowPlaylist(getters.spotifyPlaylistId)
          .then(resolve)
          .catch(error => reject(error));
      });
    },

    rollbackPlaylist(context, playlistId) {
      return new Promise((resolve, reject) => {
        SpotifyUserService.unfollowPlaylist(playlistId)
          .then(resolve)
          .catch(error => reject(error));
      });
    },

    async synchronizeFromSpotifyPlaylist({ getters, dispatch, commit }) {
      let tracks = [];

      const playlistResults = await dispatch('fetchSpotifyPlaylist');

      const initialTracks = TrackMapper
        .mapSpotifyTracksToTracksTable(playlistResults.tracks.items.map(item => item.track));

      const collectRecursiveTracks = async url => {
        const result = await SpotifyUserService.request(url);
        const recursiveTracks = TrackMapper
          .mapSpotifyTracksToTracksTable(result.items.map(item => item.track));
        if (result.next) {
          return recursiveTracks.concat(await collectRecursiveTracks(result.next));
        }
        return recursiveTracks;
      };

      if (playlistResults.tracks.next) {
        tracks = initialTracks.concat(await collectRecursiveTracks(playlistResults.tracks.next));
      } else {
        tracks = initialTracks;
      }

      if (tracks.length > 0) {
        const synchronizedTracks = await PlaylistTracksService.sync(getters.playlistId, { tracks });
        commit('updateCurrentEventTracks', { eventId: getters.currentEvent.id, tracks: synchronizedTracks });
        return synchronizedTracks;
      }
      return tracks;
    },

    syncGuestTracks({ dispatch }, tracks) {
      return new Promise((resolve, reject) => {
        if (tracks.length <= 0) {
          reject();
          return;
        }
        const uris = tracks.map(track => track.uri);
        dispatch('updateSpotifyPlaylistTracks', { uris })
          .then(() => {
            dispatch('updatePlaylistTrackTransferStatus', {
              tracks: { tracks },
            })
              .then(updatedTracks => {
                dispatch('fetchEvents');
                resolve(updatedTracks);
              });
          })
          .catch(error => reject(error));
      });
    },

    fetchSpotifyPlaylist({ getters }) {
      return new Promise((resolve, reject) => {
        SpotifyUserService.fetchPlaylist(getters.spotifyPlaylistId)
          .then(playlist => resolve(playlist))
          .catch(error => reject(error));
      });
    },
  },

  getters: {

    spotifyPlaylistId(state, getters) {
      return getters.currentEvent.playlist.spotify_id;
    },

    playlistId(state, getters) {
      return getters.currentEvent.playlist.id;
    },

  },
};
